import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { acceptSystemAgreement, getActiveSystemAgreement, getSystemAgreementState } from '../../../api/services/authService';
import { normalizeAgreementPayload, normalizeAgreementState } from '../../../utils/systemAgreement';

const isSubscriptionBlockedError = (error) => {
  const message = (error?.message || error?.response?.message || '').toLowerCase();
  const status = error?.status || error?.response?.status;
  return status === 403 || message.includes('subscription') || message.includes('subscribe');
};

const redirectToSubscriptionPlans = (navigate, returnTo) => {
  localStorage.setItem('tenantscore_subscription_state', 'required');
  navigate('/subscription-plans', {
    replace: true,
    state: returnTo ? { returnTo } : undefined,
  });
};

const AgreementGate = ({ onContinue, onCancel, roleRoute }) => {
  const navigate = useNavigate();
  const [agreementState, setAgreementState] = useState(null);
  const [agreementData, setAgreementData] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadAgreement = async () => {
      try {
        setLoading(true);
        setError('');

        if (roleRoute === '/subscription-plans') {
          onContinue?.(roleRoute);
          return;
        }

        const stateResponse = await getSystemAgreementState();
        const normalizedState = normalizeAgreementState(stateResponse);

        if (!isMounted) return;
        setAgreementState(normalizedState);

        if (!normalizedState.required) {
          onContinue?.(roleRoute);
          return;
        }

        const agreementResponse = await getActiveSystemAgreement();
        if (!isMounted) return;
        setAgreementData(normalizeAgreementPayload(agreementResponse));
      } catch (err) {
        if (!isMounted) return;

        if (isSubscriptionBlockedError(err)) {
          redirectToSubscriptionPlans(navigate, roleRoute);
          return;
        }

        setError(err.message || 'Unable to load the user agreement right now.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadAgreement();
    return () => {
      isMounted = false;
    };
  }, [navigate, onContinue, roleRoute]);

  const handleAccept = async () => {
    if (!accepted) {
      setError('Please confirm that you have read and accepted the agreement.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const response = await acceptSystemAgreement();

      if (response.status === 'success') {
        onContinue?.(roleRoute);
      } else {
        setError(response.message || 'Failed to accept the agreement.');
      }
    } catch (err) {
      if (isSubscriptionBlockedError(err)) {
        redirectToSubscriptionPlans(navigate, roleRoute);
        return;
      }

      setError(err.message || 'Failed to accept the agreement.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!agreementState?.required) {
    return null;
  }

  return (
    <div className="agreement-gate-card" style={{ maxWidth: '620px', margin: '0 auto', padding: '24px', borderRadius: '16px', background: '#fff', boxShadow: '0 8px 30px rgba(0,0,0,0.08)' }}>
      <h3 style={{ marginBottom: '8px', color: '#02236A' }}>User Agreement</h3>
      <p style={{ marginBottom: '16px', color: '#4b5563' }}>
        Please read and accept the latest platform agreement before continuing.
      </p>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', marginBottom: '16px', background: '#f8fafc' }}>
        <h4 style={{ margin: '0 0 8px', color: '#02236A' }}>{agreementData?.title || agreementState?.title || 'User agreement'}</h4>
        <p style={{ margin: '0 0 8px', color: '#4b5563', lineHeight: 1.6 }}>
          {agreementData?.summary || agreementState?.summary || 'Please read and accept the platform terms before continuing.'}
        </p>
        {(agreementData?.agreementUrl || agreementState?.agreementUrl) && (
          <a href={agreementData?.agreementUrl || agreementState?.agreementUrl} target="_blank" rel="noreferrer" style={{ color: '#0474C4', fontWeight: 600 }}>
            Open agreement document
          </a>
        )}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontWeight: 600, color: '#111827' }}>
        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
        I have read and accepted the user agreement.
      </label>
      {error ? <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div> : null}
      <div style={{ display: 'flex', gap: '12px' }}>
        <button type="button" className="btn-back" onClick={() => onCancel?.()} disabled={submitting}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={handleAccept} disabled={submitting || !accepted}>
          {submitting ? 'Accepting...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default AgreementGate;
